import { toJS, when } from 'mobx'
import { flow, getEnv, getRoot, onPatch, types } from 'mobx-state-tree'

import { getBoundingBox, radiusToZoom } from '../utils/helpers'
import { findAProviderConfigFor } from './FindAProviderConfigModel'
import {
  SEARCH_LOCATIONS,
  SEARCH_PARTNER_LOCATIONS,
  SEARCH_PARTNER_PROVIDERS,
} from './queries'

export const SearchStore = types
  .model('SearchStore', {
    providerName: '',
    filteredSpecialty: types.optional(types.string, ''),
    searchStarted: false,
    searchBarExpanded: false,
    searchHasNoResults: false,
    searchCoordinates: types.maybeNull(types.frozen()),
    autocompleteInputValue: types.optional(types.string, ''),
    filteredPartnerTags: types.optional(types.array(types.string), []),
    potentialUserLocation: types.maybeNull(
      types.model('Coordinates', { lat: types.number, lng: types.number })
    ),
    // Only look up the user's zip code when they clicked "Use current location",
    // not for the automatic request on page load
    userLocationZipRequested: false,
    filteredLanguage: types.optional(types.array(types.string), []),
    acceptsOnlineBooking: types.optional(types.boolean, false),
    distance: 10,
    searchResultLocationIds: types.optional(types.array(types.string), []),
  })
  .views(self => ({
    get root() {
      return getRoot(self)
    },
    get providerNameFilterEnabled() {
      return findAProviderConfigFor(self.root.partner)
        .isShowProviderResultsEnabled
    },
    get partnerTagsFilterEnabled() {
      if (self.root.partner) {
        const findAProviderConfig = findAProviderConfigFor(self.root.partner)
        return (
          self.root.partner.hasPartnerTags &&
          findAProviderConfig.partnerTagLabel !== undefined &&
          findAProviderConfig.partnerTagLabel !== null
        )
      }
      return false
    },
    get filteredPartnerTagsList() {
      return toJS(self.filteredPartnerTags)
    },
    get navigatorGeolocation() {
      if (getEnv(self).navigator && getEnv(self).navigator.geolocation) {
        return getEnv(self).navigator.geolocation
      }
      return null
    },
    get shouldBlockGeolocation() {
      return self.root.staticPage
    },
    get validSearchForm() {
      if (
        self.searchCoordinates !== null &&
        self.searchCoordinates !== undefined
      ) {
        return true
      }
      return false
    },
    get languageFilterEnabled() {
      return Boolean(
        self.root.partner &&
          findAProviderConfigFor(self.root.partner).isLanguageFilterEnabled
      )
    },
    get acceptsOnlineBookingFilterEnabled() {
      return (
        Boolean(self.root.partner) &&
        findAProviderConfigFor(self.root.partner)
          .isAcceptsOnlineBookingFilterEnabled !== false
      )
    },
    get acceptsOnlineBookingFilterActive() {
      return self.acceptsOnlineBookingFilterEnabled && self.acceptsOnlineBooking
    },
    get zoomDistance() {
      return radiusToZoom(self.distance)
    },
    get boundingBox() {
      if (
        self.searchCoordinates !== null &&
        self.searchCoordinates !== undefined
      ) {
        return getBoundingBox(
          self.searchCoordinates.lat,
          self.searchCoordinates.lng,
          self.zoomDistance
        )
      }
      return null
    },
    get isProviderSearchQuery() {
      if (
        findAProviderConfigFor(self.root.partner).isShowProviderResultsEnabled
      ) {
        return SEARCH_PARTNER_PROVIDERS
      }
      return SEARCH_PARTNER_LOCATIONS
    },
    get isPartnerSearchQuery() {
      if (self.root.partner) {
        return this.isProviderSearchQuery
      }
      return SEARCH_LOCATIONS
    },
  }))
  .actions(self => ({
    afterCreate() {
      onPatch(self, patch => {
        when(
          () => patch && patch.path === '/distance' && patch.value,
          () => {
            self.searchLocations()
          }
        )
      })
      // Automatically request user location on page load
      self.requestUserLocationOnLoad()
    },
    requestUserLocationOnLoad() {
      if (self.shouldBlockGeolocation) {
        self.root.debug('Skipping geolocation request for static page')
        return
      }
      if (!self.navigatorGeolocation) {
        getEnv(self).logger.debug(
          'Geolocation is currently unavailable for this device'
        )
        return
      }

      // Check if Permissions API is available to query permission state
      const permissions = getEnv(self).navigator?.permissions
      if (permissions) {
        permissions
          .query({ name: 'geolocation' })
          .then(permissionStatus => {
            if (permissionStatus.state === 'granted') {
              // Permission already granted, request location immediately
              getEnv(self).logger.debug(
                'Geolocation permission already granted, requesting location'
              )
              self.requestUserLocation()
            } else if (permissionStatus.state === 'prompt') {
              // Permission not yet decided, request location (will show browser prompt)
              getEnv(self).logger.debug(
                'Requesting geolocation permission on page load'
              )
              self.requestUserLocation()
            }
            // If 'denied', do nothing - user has already denied permission
          })
          .catch(error => {
            // Permissions API failed, fall back to requesting directly
            getEnv(self).logger.debug(
              'Permissions API unavailable, requesting geolocation directly'
            )
            self.requestUserLocation()
          })
      } else {
        // No Permissions API, request directly
        getEnv(self).logger.debug(
          'Permissions API unavailable, requesting geolocation directly'
        )
        self.requestUserLocation()
      }
    },
    setAutocompleteInputValue(value) {
      self.autocompleteInputValue = value
    },
    setPotentialUserLocation(value) {
      self.potentialUserLocation = value
    },
    setUserLocationZipRequested(value) {
      self.userLocationZipRequested = value
    },
    setProviderName(providerName) {
      self.providerName = providerName
    },
    setSpecialty(value) {
      self.filteredSpecialty = value
    },
    setFilteredLanguage(language) {
      self.filteredLanguage = language
    },
    setAcceptsOnlineBooking(value) {
      self.acceptsOnlineBooking = value
      if (self.searchStarted) {
        self.searchLocations()
      }
    },
    clearFilters() {
      self.setFilterByDate('false')
      self.setFilterByTime('false')
      self.setTime(undefined)
      self.setDate(undefined)
    },
    toggleSearchStarted() {
      self.searchStarted = !self.searchStarted
    },
    toggleSearchBarExpansion() {
      self.searchBarExpanded = !self.searchBarExpanded
    },
    resetProviderName() {
      self.setProviderName('')
    },
    setFilteredPartnerTags(value) {
      self.filteredPartnerTags = value
    },
    setSearchCoordinates(value) {
      self.searchCoordinates = value
    },
    setDistance(value) {
      self.distance = value
    },
    requestUserLocation({ requestedByUser = false } = {}) {
      if (self.shouldBlockGeolocation) {
        self.root.debug('Skipping geolocation request for static page')
        return
      }
      if (!self.navigatorGeolocation) {
        getEnv(self).logger.debug(
          'Geolocation is currently unavailable for this device'
        )
      } else {
        getEnv(self).logger.debug('Requesting user location')
        self.navigatorGeolocation.getCurrentPosition(
          ({ coords: { latitude: lat, longitude: lng } }) => {
            // Remove any previously searched address so the field doesn't
            // suggest the results are still for that address. Only on an
            // explicit request, so the page-load lookup can't wipe an
            // address the user picked while it was pending
            if (requestedByUser) {
              self.setAutocompleteInputValue('')
            }
            self.setUserLocationZipRequested(requestedByUser)
            self.setPotentialUserLocation({ lat, lng })
            self.searchWithPotentialUserLocation()
          },
          error => getEnv(self).logger.error(error),
          { enableHighAccuracy: true }
        )
      }
    },
    searchWithPotentialUserLocation() {
      if (self.potentialUserLocation) {
        self.setSearchCoordinates({
          lat: self.potentialUserLocation.lat,
          lng: self.potentialUserLocation.lng,
        })
        self.searchLocations()
      }
    },
    searchLocations: flow(function* searchLocations() {
      try {
        if (self.root.partnerId && !self.root.partner) {
          self.root.debug('WAITING FOR PARTNER CONFIG BEFORE SEARCH')
          yield self.root.fetchPartner()
        }
        if (self.root.loading) {
          return undefined
        }
        if (self.boundingBox === null) {
          return undefined
        }
        self.root.setLoading(true)
        self.searchStarted = true
        self.searchHasNoResults = false
        self.root.clearLocations()
        self.root.clearProviders()
        self.clearSearchResultLocationIds()

        const searchQuery = self.isPartnerSearchQuery

        const searchParams = self.getSearchParams(self.boundingBox)

        self.root.debug(`SEARCH PARAMS: ${JSON.stringify(searchParams)}`)

        const { searchLocations } = yield self.root.query(
          searchQuery,
          searchParams,
          { fetchPolicy: 'network-only' }
        )

        self.setSearchResultLocationIdsFromResponse(searchLocations)

        if (searchLocations && searchLocations.locations.length < 1) {
          self.searchHasNoResults = true
        }
      } catch (error) {
        getEnv(self).logger.error(error)
      } finally {
        self.root.setMapCenter(self.boundingBox)
        self.root.setLoading(false)
      }
    }),
    searchByBounds: flow(function* searchByBounds(bounds) {
      try {
        if (self.root.partnerId && !self.root.partner) {
          self.root.debug('WAITING FOR PARTNER CONFIG BEFORE BOUNDS SEARCH')
          yield self.root.fetchPartner()
        }
        if (self.root.loading) {
          return undefined
        }
        self.root.setLoading(true)
        self.searchHasNoResults = false
        self.root.clearLocations()
        self.root.clearProviders()
        self.clearSearchResultLocationIds()

        const searchQuery = self.isPartnerSearchQuery

        const searchParams = self.getSearchParams(bounds)

        self.root.debug(
          `SEARCH BY BOUNDS PARAMS: ${JSON.stringify(searchParams)}`
        )

        const { searchLocations } = yield self.root.query(
          searchQuery,
          searchParams,
          { fetchPolicy: 'network-only' }
        )

        self.setSearchResultLocationIdsFromResponse(searchLocations)

        if (searchLocations && searchLocations.locations.length < 1) {
          self.searchHasNoResults = true
        }
      } catch (error) {
        getEnv(self).logger.error(error)
      } finally {
        self.root.setMapCenter(bounds)
        self.root.setLoading(false)
      }
    }),
    getSearchParams(bounds) {
      const searchParams = {
        boundingBox: bounds,
      }

      if (self.root.partner) {
        searchParams.partnerId = self.root.partner.id
        if (self.acceptsOnlineBookingFilterActive) {
          searchParams.acceptsOnlineBooking = self.acceptsOnlineBooking
        }
      }

      return searchParams
    },
    clearSearchResultLocationIds() {
      self.searchResultLocationIds = []
    },
    setSearchResultLocationIdsFromResponse(searchLocations) {
      const locations = searchLocations?.locations
      if (!locations?.length) {
        self.searchResultLocationIds = []
        return
      }
      self.searchResultLocationIds = locations
        .map(location => location?.id)
        .filter(Boolean)
    },
  }))
